export interface CodeItem {
  code: string;
  description: string | null;
  orderIndex: number;
  /** The AI's inline flag tooltip for this code, when its check raised one. */
  aiFlagText?: string | null;
}

export type DiffStatus = "added" | "removed" | "changed" | "unchanged";

export interface CodeItemWithDiff extends CodeItem {
  diffStatus?: DiffStatus;
  positionDirection?: "up" | "down";
}

/**
 * Compares a code against the AI codes to determine its diff status.
 * Returns the code items with diff statuses attached.
 */
export function computeCodeDiff(humanCodes: CodeItem[], aiCodes: CodeItem[]): CodeItemWithDiff[] {
  // Create maps for quick lookup by code
  const aiCodeMap = new Map(aiCodes.filter((c) => c.code).map((c) => [c.code, c] as const));

  // Process human codes
  return humanCodes.map((humanCode) => {
    const aiCode = humanCode.code ? aiCodeMap.get(humanCode.code) : undefined;

    if (!aiCode) {
      // Code not found in AI - it was added by human
      return { ...humanCode, diffStatus: "added" as DiffStatus };
    }

    // Code exists in both - check if properties changed
    // Handle undefined/null comparisons properly
    const orderIndexChanged = (humanCode.orderIndex ?? -1) !== (aiCode.orderIndex ?? -1);

    // If only position changed (orderIndex), mark with direction but keep unchanged status
    if (orderIndexChanged) {
      const humanOrderIndex = humanCode.orderIndex ?? -1;
      const aiOrderIndex = aiCode.orderIndex ?? -1;
      // Lower orderIndex = earlier position in list (moved up)
      // Higher orderIndex = later position in list (moved down)
      const positionDirection: "up" | "down" = humanOrderIndex < aiOrderIndex ? "down" : "up";
      return {
        ...humanCode,
        diffStatus: "unchanged" as DiffStatus,
        positionDirection,
      };
    }

    // Code is identical
    return { ...humanCode, diffStatus: "unchanged" as DiffStatus };
  });
}

/**
 * Finds codes that exist in AI but not in human review (removed codes).
 */
export function findRemovedCodes(aiCodes: CodeItem[], humanCodes: CodeItem[]): CodeItemWithDiff[] {
  const humanCodeSet = new Set(humanCodes.filter((c) => c.code).map((c) => c.code!));

  return aiCodes
    .filter((code) => code.code && !humanCodeSet.has(code.code))
    .map((code) => ({ ...code, diffStatus: "removed" as DiffStatus }));
}

/**
 * Computes the full diff between AI codes and human codes for both procedures and diagnoses.
 */
export function computeFullCodeDiff(
  humanProcedures: CodeItem[],
  humanDiagnoses: CodeItem[],
  aiProcedures: CodeItem[],
  aiDiagnoses: CodeItem[],
): {
  procedures: CodeItemWithDiff[];
  diagnoses: CodeItemWithDiff[];
  removedProcedures: CodeItemWithDiff[];
  removedDiagnoses: CodeItemWithDiff[];
} {
  // Compute diff for codes present in human review
  const procedures = computeCodeDiff(humanProcedures, aiProcedures);
  const diagnoses = computeCodeDiff(humanDiagnoses, aiDiagnoses);

  // Find removed codes (present in AI but not in human)
  const removedProcedures = findRemovedCodes(aiProcedures, humanProcedures);
  const removedDiagnoses = findRemovedCodes(aiDiagnoses, humanDiagnoses);

  return {
    procedures,
    diagnoses,
    removedProcedures,
    removedDiagnoses,
  };
}
